import { default as qs } from 'qs';
import api from '../../../utils/API';
import {
  GetCategoriesRequest,
  getOptimizationAnalysisVariationRequest as getOptimizationAnalysisRevenueChartRequest,
  getOptimizationBigNumbersRequest,
  GetOptimizationPriceRequest,
  ListOptimizedStoresCategoriesResponse,
  OptimizationHistoryBigNumbersRequest,
  OptimizationHistoryBigNumbersResponse,
  OptimizationHistoryDownloadTableRequest,
  OptimizationHistoryHistogramChartRequest,
  OptimizationHistoryHistogramChartResponse,
  OptimizationHistoryTableResponse,
  OptimizedStoresCategory,
  SaveOptimizationPriceRequest,
  SearchScenarioParams,
} from './types/service';

export const getCategories = ({ params, nivel = 'categoryLevel4' }: GetCategoriesRequest) =>
  api({
    url: `${process.env.REACT_APP_IPA_URL}/products/filters`,
    method: 'POST',
    expectToken: true,
    params: {
      clientId: JSON.parse(localStorage.getItem('cliente') ?? '{}')?.codigo || null,
      size: params?.size ? params.size : 20,
    },
    data: {
      field: nivel || null,
      ...(params?.query && { query: params.query }),
    },
  }).then(resp => resp.data);

export const saveOptimization = (data: SaveOptimizationPriceRequest) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization`,
    method: 'POST',
    expectToken: true,
    data,
  }).then(resp => resp.data);

export const updateOptimization = (id: string, data: SaveOptimizationPriceRequest) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/${id}`,
    method: 'PUT',
    expectToken: true,
    data,
  }).then(resp => resp.data);

export const getOptimizationById = (id: string) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/${id}`,
    method: 'GET',
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizations = ({ params, data }: { params: SearchScenarioParams; data: GetOptimizationPriceRequest }) =>
  api({
    url:
      `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search?page=${params?.page || 0}` +
      `&size=${params?.size || 10}&sort=${params?.sort || ''},${params?.order || 'asc'}`.trim(),
    method: 'POST',
    data,
    expectToken: true,
  }).then(resp => resp.data);

export const deleteOptimization = (id: string) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/${id}`,
    method: 'DELETE',
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationResult = (id: string) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/result-page/${id}`,
    method: 'GET',
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationBigNumbers = ({ id, stores_id }: { id: string; stores_id: getOptimizationBigNumbersRequest }) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search-optimized-price-forecast/${id}
        `,
    method: 'POST',
    data: {
      stores_id: stores_id,
    },
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationChartData = ({ id, stores_id }: { id: string; stores_id: getOptimizationBigNumbersRequest }) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search-optimized-projection-range/${id}
        `,
    method: 'POST',
    data: {
      stores_id: stores_id,
    },
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationAnalysisRevenueChart = (data: getOptimizationAnalysisRevenueChartRequest & { id: string }) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search-revenue-variation-data/${data.id}
        `,
    method: 'POST',
    data: data,
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationAnalysisProfitChart = (data: getOptimizationAnalysisRevenueChartRequest & { id: string }) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search-profit-variation-data/${data.id}
        `,
    method: 'POST',
    data: data,
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationHistoryBigNumbers = (data: OptimizationHistoryBigNumbersRequest & { id: string }): Promise<OptimizationHistoryBigNumbersResponse> =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search-price-history/${data.id}
        `,
    method: 'POST',
    data: data,
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationHistoryHistogramChart = (
  data: OptimizationHistoryHistogramChartRequest & { id: string },
): Promise<OptimizationHistoryHistogramChartResponse> =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search-price-histogram-data/${data.id}
        `,
    method: 'POST',
    data: data,
    expectToken: true,
  }).then(resp => resp.data);

export const getOptimizationHistoryTableData = (
  data: OptimizationHistoryHistogramChartRequest & { id: string } & { page: number; size: number; sort: string; order: string },
): Promise<OptimizationHistoryTableResponse> =>
  api({
    url:
      `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/search-price-history-table-data/${data.id}?page=${data.page}` +
      `&size=${data.size}&sort=${data.sort},${data.order}`,
    method: 'POST',
    data: data,
    expectToken: true,
  }).then(resp => resp.data);

export const downloadOptimization = (data: OptimizationHistoryDownloadTableRequest & { id: string }) =>
  api({
    url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/download-price-history-table/${data.id}`,
    method: 'POST',
    download: true,
    responseType: 'arraybuffer',
    expectToken: true,
    data: data,
  })
    .then(resp => resp.data)
    .then(download => {
      if (download.byteLength > 0) {
        const blob = new Blob([download]);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = window.URL.createObjectURL(blob);
        hiddenElement.target = '_blank';
        hiddenElement.download = `historico-de-precos.${data.file_type}`;
        hiddenElement.click();
      }
    });

export const listResponsible = async (query?: string): Promise<DataItem[]> => {
  const res = await api.get<string[]>(`${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/responsible`, {
    expectToken: true,
    params: { search: query },
    paramsSerializer(params) {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  return (
    res.data.map(item => ({
      label: item,
      value: item,
    })) || []
  );
};

export const listOptimizedStoresCategories = async (): Promise<OptimizedStoresCategory[]> => {
  const res = await api.get<ListOptimizedStoresCategoriesResponse>(`${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/optimized-stores-categories`, {
    expectToken: true,
  });

  const optimizedStoresCategories = res.data?.optimized_stores_categories ? res.data.optimized_stores_categories : [];

  return optimizedStoresCategories;
};

export const getOptimizationPriceManagerDetails = (id: string) =>
    api({
        url: `${process.env.REACT_APP_DEMAND_FORECAST}/v2/optimization/price-manager/details/${id}`,
        method: 'GET',
        expectToken: true,
    }).then((resp) => resp.data);
